// P7 (2026-11-02) — Public customer signup page.
//
// Prereq: /api/brand-os/public/signup/status returns { enabled: bool }.
// When disabled, show a "Signups are temporarily closed" notice.
// When enabled, show the form (email + password OR magic link),
// a honeypot field hidden from humans, and Terms/Privacy consent.

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, formatApiError } from "../lib/api";

export default function Signup() {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(null);
  const [mode, setMode] = useState("password");
  const [form, setForm] = useState({
    email: "", password: "", org_name: "", honeypot: "",
    terms: false, privacy: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/signup/status");
        setEnabled(!!r.data?.enabled);
      } catch {
        setEnabled(false);
      }
    })();
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.terms || !form.privacy) {
      setErr("Please accept the Terms and Privacy Policy.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const r = await api.post("/public/signup", {
        email: form.email.trim(),
        password: mode === "password" ? form.password : undefined,
        request_magic_link: mode === "magic_link",
        org_name: form.org_name.trim(),
        honeypot: form.honeypot,
        terms_accepted: true,
        privacy_accepted: true,
      });
      setDone(r.data || {});
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setBusy(false); }
  }

  if (enabled === null) {
    return (
      <div className="bo-root">
        <div className="bo-login-shell">
          <div className="bo-login-card" data-testid="signup-loading">
            Loading…
          </div>
        </div>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="bo-root">
        <div className="bo-login-shell">
          <div className="bo-login-card" data-testid="signup-disabled">
            <div className="bo-login-eyebrow">Precipice Brand OS</div>
            <h1 className="bo-login-title">Signups are temporarily closed</h1>
            <p>
              We're reviewing new workspace requests manually. Please check
              back soon.
            </p>
            <p>
              <Link to="/brand-os/login" data-testid="signup-to-login">
                Already have an account? Sign in →
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="bo-root">
        <div className="bo-login-shell">
          <div className="bo-login-card" data-testid="signup-done">
            <div className="bo-login-eyebrow">Precipice Brand OS</div>
            <h1 className="bo-login-title">Check your email</h1>
            <p>
              We sent a verification link to <strong>{form.email}</strong>.
              The link is valid for 7 days.
            </p>
            {done.verification_token_preview && (
              <p
                data-testid="signup-preview-token"
                style={{
                  background: "#f6f3ea", padding: 10, borderRadius: 6,
                  fontSize: 12, fontFamily: "monospace",
                  wordBreak: "break-all",
                }}
              >
                Preview mode — your verification token:{" "}
                <code>{done.verification_token_preview}</code>
              </p>
            )}
            <p>
              <Link
                to={`/brand-os/verify-email${done.verification_token_preview
                      ? `?token=${encodeURIComponent(done.verification_token_preview)}`
                      : ""}`}
                data-testid="signup-done-verify-link"
              >
                Open verification page →
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bo-root" data-testid="signup-screen">
      <div className="bo-login-shell">
        <form className="bo-login-card" onSubmit={onSubmit}
              data-testid="signup-form">
          <div className="bo-login-eyebrow">Precipice Brand OS</div>
          <h1 className="bo-login-title">Create a workspace</h1>
          <p style={{ color: "var(--bo-muted)", marginBottom: 10 }}>
            One workspace per team. You'll be the owner.
          </p>

          <label className="bo-label">Workspace name</label>
          <input
            className="bo-input"
            data-testid="signup-org-name"
            value={form.org_name}
            onChange={(e) => setForm({ ...form, org_name: e.target.value })}
            placeholder="Acme Health"
            required
          />

          <label className="bo-label" style={{ marginTop: 10 }}>Email</label>
          <input
            className="bo-input"
            data-testid="signup-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />

          <div style={{ display: "flex", gap: 8, marginTop: 10,
                          fontSize: 12 }}>
            <button
              type="button"
              className={mode === "password" ? "bo-btn" : "bo-btn bo-btn-ghost"}
              onClick={() => setMode("password")}
              data-testid="signup-mode-password"
            >
              Password
            </button>
            <button
              type="button"
              className={mode === "magic_link" ? "bo-btn" : "bo-btn bo-btn-ghost"}
              onClick={() => setMode("magic_link")}
              data-testid="signup-mode-magic-link"
            >
              Magic link
            </button>
          </div>

          {mode === "password" && (
            <>
              <label className="bo-label" style={{ marginTop: 10 }}>
                Password (10+ chars)
              </label>
              <input
                className="bo-input"
                data-testid="signup-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                minLength={10}
                required
              />
            </>
          )}

          {/* Honeypot: must stay empty for a human submission. */}
          <input
            type="text"
            name="company_website"
            value={form.honeypot}
            onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
            tabIndex="-1"
            autoComplete="off"
            aria-hidden="true"
            data-testid="signup-honeypot"
            style={{ position: "absolute", left: "-9999px",
                      width: 1, height: 1, opacity: 0 }}
          />

          <label style={{ display: "flex", gap: 8, marginTop: 12,
                          alignItems: "flex-start", fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.terms}
              onChange={(e) => setForm({ ...form, terms: e.target.checked })}
              data-testid="signup-accept-terms"
            />
            <span>
              I accept the{" "}
              <a href="/api/legal/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>.
            </span>
          </label>
          <label style={{ display: "flex", gap: 8, marginTop: 6,
                          alignItems: "flex-start", fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.privacy}
              onChange={(e) => setForm({ ...form, privacy: e.target.checked })}
              data-testid="signup-accept-privacy"
            />
            <span>
              I accept the{" "}
              <a href="/api/legal/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>.
            </span>
          </label>

          {err && (
            <div className="bo-error" data-testid="signup-error"
                 style={{ marginTop: 10, color: "crimson" }}>
              {typeof err === "string" ? err : err.message || JSON.stringify(err)}
            </div>
          )}

          <button
            type="submit"
            className="bo-btn"
            data-testid="signup-submit"
            disabled={busy}
            style={{ width: "100%", marginTop: 16 }}
          >
            {busy ? "Creating workspace…" : "Create workspace"}
          </button>

          <p style={{ marginTop: 10, fontSize: 13 }}>
            <Link to="/brand-os/login" data-testid="signup-have-account">
              Already have an account? Sign in →
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
